import type { DataOption } from '@/common.types'
import type { FilterRuleModel, GlobalPCSetModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import { useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { find, map, pipe, prop } from 'lodash/fp'
import { InfoIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	PricingEngineType,
	useGlobalPcSetsQuery,
} from '@/entities/pricing-campaign'

import {
	FormProvider,
	RHFCombobox,
	RHFRadioCardsGroup,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { FieldGroup, FieldSet } from '@/shared/components/ui/field'
import {
	Modal,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { useModals } from '@/shared/lib/modal-manager'

const getDraftSchema = (t: TFunction) =>
	z.object({
		name: z
			.string()
			.min(
				1,
				t('validation.required_filed', { defaultValue: 'Field is required' })
			),
		optimization_group_id: z
			.string()
			.min(
				1,
				t('validation.required_filed', { defaultValue: 'Field is required' })
			)
			.nullable(),
		global_pc_set_id: z
			.string()
			.min(
				1,
				t('validation.required_filed', { defaultValue: 'Field is required' })
			)
			.nullable(),
		engine: z.enum([
			PricingEngineType.RB,
			PricingEngineType.ML,
			PricingEngineType.MD,
			PricingEngineType.Promo,
		]),
	})
type PricingCampaignFormData = z.infer<ReturnType<typeof getDraftSchema>>

const getEngineOptions = (t: TFunction) => [
	{
		label: t(PricingEngineType.RB, { defaultValue: 'Rule-based' }),
		description: t(`${PricingEngineType.RB}.description`, {
			defaultValue: `${PricingEngineType.RB}.description`,
		}),
		value: PricingEngineType.RB,
	},
	{
		label: t(PricingEngineType.ML, { defaultValue: 'Demand-based' }),
		description: t(`${PricingEngineType.ML}.description`, {
			defaultValue: `${PricingEngineType.ML}.description`,
		}),
		value: PricingEngineType.ML,
	},
	{
		label: t(PricingEngineType.MD, { defaultValue: 'Markdown' }),
		description: t(`${PricingEngineType.MD}.description`, {
			defaultValue: `${PricingEngineType.MD}.description`,
		}),
		value: PricingEngineType.MD,
	},
	{
		label: t(PricingEngineType.Promo, { defaultValue: 'Promo' }),
		description: t(`${PricingEngineType.Promo}.description`, {
			defaultValue: `${PricingEngineType.Promo}.description`,
		}),
		value: PricingEngineType.Promo,
	},
]
function CreatePricingCampaignModalContent({
	globalPcSetId,
	close,
	isOpen,
	optimizationGroupId,
	scenarioId,
	draft,
	onCreateCampaign,
	disableOptimizationGroups,
}: CreatePricingCampaignModalProps) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const optimizationGroupsQuery = useOptimizationGroupsQuery({
		select: map((og) => ({ value: og.id, label: og.name })),
	})
	const { data: mainGlobalPcSetId } = useGlobalPcSetsQuery({
		select: pipe(find<GlobalPCSetModel>({ is_main: true }), prop('id')),
	})
	const optimizationGroupsOptions = useMemo(
		() =>
			optimizationGroupsQuery.data
				? [
						{ value: null, label: t('global', { defaultValue: 'Global' }) },
						...optimizationGroupsQuery.data,
					]
				: [{ value: null, label: t('global', { defaultValue: 'Global' }) }],
		[optimizationGroupsQuery.data]
	)
	const methods = useForm<PricingCampaignFormData>({
		defaultValues: {
			optimization_group_id: optimizationGroupId || null,
			global_pc_set_id: globalPcSetId || null,
			name: '',
			engine: PricingEngineType.RB,
		},
		resolver: zodResolver(getDraftSchema(t)),
	})
	const _handleSubmit = ({
		name,
		engine,
		optimization_group_id,
	}: PricingCampaignFormData) => {
		/**
		 * If optimization group is selected, create a local pricing campaign in the optimization group.
		 * Otherwise, create a global pricing campaign in the provided global PC set,
		 * falling back to the main global PC set.
		 */
		const data = optimization_group_id
			? {
					optimizationGroupId: optimization_group_id,
					scenarioId,
					draft: { ...(draft || {}), name, engine },
				}
			: {
					globalPcSetId: globalPcSetId || mainGlobalPcSetId,
					draft: { ...(draft || {}), name, engine },
				}
		if (onCreateCampaign) {
			onCreateCampaign?.(data)
		} else {
			showModal('PRICING_CAMPAIGN_MODAL', data)
		}
		close()
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent data-testid='create-pc-modal'>
				<ModalHeader>
					<ModalTitle>
						{t('pc.create.title', {
							defaultValue: 'Create a pricing campaign',
						})}
					</ModalTitle>
				</ModalHeader>
				<FormProvider
					methods={methods}
					onSubmit={handleSubmit}
					/* className='grid gap-6' */
				>
					<FieldSet>
						<FieldGroup>
							<RHFCombobox
								label={t('optimization_group', {
									defaultValue: 'Optmization group',
								})}
								className='w-full'
								disabled={disableOptimizationGroups}
								isLoading={optimizationGroupsQuery.isFetching}
								name='optimization_group_id'
								options={optimizationGroupsOptions as DataOption[]}
							/>
							<RHFTextField
								className='w-full'
								autoComplete='off'
								label={t('pricing_campaign_name', {
									defaultValue: 'Pricing campaign name',
								})}
								name='name'
								placeholder={t('pricing_campaign_name_placeholder')}
							/>
							<RHFRadioCardsGroup
								label={t('pricing_engine', { defaultValue: 'Pricing engine' })}
								name='engine'
								options={getEngineOptions(t)}
							/>
							<Alert variant='info'>
								<InfoIcon />
								<AlertDescription>
									{t('pc.create.info', {
										defaultValue:
											'Note, that products gonna be assigned to the new pricing campaign only after the optimization group is updated',
									})}
								</AlertDescription>
							</Alert>
						</FieldGroup>
					</FieldSet>
				</FormProvider>
				<ModalFooter className='justify-between'>
					<ModalClose asChild>
						<Button data-testid='create-pc-modal-cancel'>
							{t('general_cancel')}
						</Button>
					</ModalClose>
					<Button
						variant='primary'
						onClick={handleSubmit}
						data-testid='create-pc-modal-submit'
					>
						{t('general_create')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}
type CreatePricingCampaignModalProps = {
	globalPcSetId?: string
	isOpen: boolean
	close(): void
	optimizationGroupId?: string | null
	disableOptimizationGroups?: boolean
	scenarioId?: string
	draft?: {
		name?: string
		engine?: string
		assignment_filters?: FilterRuleModel[]
	}
	onCreateCampaign?(temp: {
		optimizationGroupId?: string | null
		scenarioId?: string
		draft?: {
			name?: string
			engine?: string
			assignment_filters?: FilterRuleModel[]
		}
	}): void
}
export const CreatePricingCampaignModal = (
	props: CreatePricingCampaignModalProps
) => <CreatePricingCampaignModalContent {...props} />
